import React, { useEffect, useMemo, useState } from 'react';
import { Send, Trash2, Copy, Inbox, RefreshCw, CheckCircle2, AlertTriangle, Zap, ZapOff } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import {
  runRenewalScheduler, getQueue, dismissDraft, markDraftSent, markDraftFailed, clearSent,
} from './renewalScheduler';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ status }) => {
  const map = {
    draft: { label: 'Draft', cls: 'bg-[#FFEDD6] text-[#1F0068]' },
    sent: { label: 'Sent', cls: 'bg-[#00b67a]/15 text-[#00b67a]' },
    failed: { label: 'Failed', cls: 'bg-[#FF5C39]/15 text-[#FF5C39]' },
  };
  const s = map[status] || map.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.cls}`}>{s.label}</span>;
};

const RenewalQueueWidget = ({ policies }) => {
  const { toast } = useToast();
  const [queue, setQueue] = useState(() => getQueue());
  const [resendEnabled, setResendEnabled] = useState(false);
  const [busy, setBusy] = useState('');

  const refresh = () => setQueue(getQueue());

  // Run the daily scheduler on mount (idempotent — guarded to run once per calendar day).
  useEffect(() => {
    const result = runRenewalScheduler(policies || []);
    if (result.drafted > 0) {
      toast({ title: 'Renewal drafts prepared', description: `${result.drafted} renewal email${result.drafted === 1 ? '' : 's'} drafted for policies expiring in the next 30 days.` });
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check backend Resend status once so we know whether to enable the Send buttons.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/emails/status`);
        const j = await res.json();
        if (!cancelled) setResendEnabled(!!j.resend_enabled);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const runNow = () => {
    const result = runRenewalScheduler(policies || [], { force: true });
    refresh();
    toast({ title: 'Scheduler run complete', description: `${result.drafted} new draft${result.drafted === 1 ? '' : 's'} added.` });
  };

  const dismiss = (id) => { dismissDraft(id); refresh(); };
  const clearAllSent = () => { clearSent(); refresh(); };

  const copyDraft = async (d) => {
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const htmlBlob = new Blob([d.html], { type: 'text/html' });
        const textBlob = new Blob([`Subject: ${d.subject}\n\n${d.text}`], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
      } else {
        await navigator.clipboard.writeText(`Subject: ${d.subject}\n\n${d.text}`);
      }
      toast({ title: 'Draft copied', description: `Paste into your email tool to send to ${d.customerName}.` });
    } catch {
      toast({ title: 'Could not copy the draft' });
    }
  };

  const sendOne = async (d) => {
    if (!d.customerEmail) {
      toast({ title: 'No customer email on file', description: `Update the customer record for ${d.customerName} first.` });
      return;
    }
    setBusy(d.id);
    try {
      const res = await fetch(`${BACKEND}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: d.customerEmail,
          subject: d.subject,
          html: d.html,
          text: d.text,
          tag: 'renewal',
        }),
      });
      const j = await res.json();
      if (res.ok && j.status === 'sent') {
        markDraftSent(d.id, { provider: 'resend' });
        toast({ title: 'Renewal email sent', description: `Delivered to ${d.customerEmail}.` });
      } else if (res.ok && j.status === 'queued') {
        markDraftFailed(d.id, j.detail || 'Queued (no API key)');
        toast({ title: 'Queued (Resend not configured yet)', description: 'Add RESEND_API_KEY + RESEND_SENDER to backend/.env, then click Send again.' });
      } else {
        markDraftFailed(d.id, j.detail || 'Send failed');
        toast({ title: 'Send failed', description: j.detail || 'Please retry.' });
      }
      refresh();
    } catch (e) {
      markDraftFailed(d.id, String(e));
      refresh();
      toast({ title: 'Send failed', description: String(e) });
    } finally {
      setBusy('');
    }
  };

  const sendAllDrafts = async () => {
    const drafts = queue.filter((d) => d.status === 'draft');
    if (drafts.length === 0) return;
    if (!window.confirm(`Attempt to send ${drafts.length} renewal email${drafts.length === 1 ? '' : 's'} now via Resend?`)) return;
    for (const d of drafts) {
      // eslint-disable-next-line no-await-in-loop
      await sendOne(d);
    }
  };

  const drafts = useMemo(() => queue.filter((d) => d.status === 'draft'), [queue]);
  const sent = useMemo(() => queue.filter((d) => d.status === 'sent'), [queue]);
  const failed = useMemo(() => queue.filter((d) => d.status === 'failed'), [queue]);

  return (
    <div data-testid="renewal-queue-widget" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#FF5C39]">
            <Inbox className="w-4 h-4" /> Renewal email queue
          </div>
          <h3 className="text-lg font-bold text-[#1F0068] mt-1">
            {drafts.length} draft{drafts.length === 1 ? '' : 's'} · {sent.length} sent · {failed.length} failed
          </h3>
          <p className="text-xs text-[#1F0068]/70 mt-1">Runs automatically once a day. Every policy renewing in the next 30 days gets an auto-drafted designed email that you can review, copy or send.</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <div data-testid="resend-status-pill" className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold ${resendEnabled ? 'bg-[#00b67a]/15 text-[#00b67a]' : 'bg-[#FFF6EB] text-[#1F0068]/70'}`}>
            {resendEnabled ? <Zap className="w-3 h-3" /> : <ZapOff className="w-3 h-3" />}
            {resendEnabled ? 'Resend live' : 'Copy-paste only'}
          </div>
          <Button data-testid="run-scheduler-btn" onClick={runNow} variant="outline" size="sm" className="rounded-full h-8 gap-1 text-xs"><RefreshCw className="w-3 h-3" /> Run now</Button>
          {drafts.length > 0 && (
            <Button data-testid="send-all-btn" onClick={sendAllDrafts} size="sm" className="rounded-full h-8 gap-1 text-xs bg-[#FF5C39] hover:bg-[#e0492b] text-white">
              <Send className="w-3 h-3" /> Send all
            </Button>
          )}
        </div>
      </div>

      {queue.length === 0 && (
        <div className="mt-4 text-sm text-[#1F0068]/70 bg-[#FFF6EB] rounded-xl p-4" data-testid="queue-empty">
          No drafts yet. Once a policy is inside its 30-day renewal window, the scheduler will auto-draft a designed email here.
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/50 font-bold">Drafts</div>
          {drafts.slice(0, 6).map((d) => (
            <div key={d.id} data-testid={`queue-row-${d.id}`} className="bg-[#FFF6EB] rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-[#1F0068] truncate">{d.customerName} <span className="text-[#1F0068]/60 text-xs">· {d.planNumber}</span></div>
                <div className="text-xs text-[#1F0068]/70">{d.customerEmail || 'no email on file'} · renews {new Date(d.renewalDate).toLocaleDateString('en-GB')}</div>
              </div>
              <div className="flex gap-1.5 shrink-0 items-center">
                <StatusPill status="draft" />
                <Button size="sm" variant="outline" onClick={() => copyDraft(d)} className="rounded-full h-8 text-xs gap-1"><Copy className="w-3 h-3" /> Copy</Button>
                <Button
                  data-testid={`queue-send-${d.id}`}
                  size="sm"
                  onClick={() => sendOne(d)}
                  disabled={busy === d.id}
                  className="rounded-full h-8 text-xs gap-1 bg-[#1F0068] hover:bg-[#2A0C7A] text-white"
                >
                  <Send className="w-3 h-3" /> {busy === d.id ? 'Sending…' : 'Send'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss(d.id)} className="rounded-full h-8 w-8 p-0 text-[#1F0068]/60"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
          {drafts.length > 6 && <div className="text-xs text-[#1F0068]/70">+ {drafts.length - 6} more drafts</div>}
        </div>
      )}

      {sent.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/50 font-bold">Sent recently</div>
            <Button size="sm" variant="ghost" onClick={clearAllSent} className="rounded-full h-7 text-[11px] text-[#1F0068]/60">Clear sent</Button>
          </div>
          {sent.slice(0, 4).map((d) => (
            <div key={d.id} className="bg-[#00b67a]/5 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#1F0068] truncate flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#00b67a]" /> {d.customerName}</div>
                <div className="text-xs text-[#1F0068]/70">{d.customerEmail} · sent {new Date(d.sentAt).toLocaleString('en-GB')}</div>
              </div>
              <StatusPill status="sent" />
            </div>
          ))}
        </div>
      )}

      {failed.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Needs attention</div>
          {failed.map((d) => (
            <div key={d.id} className="bg-[#FF5C39]/5 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#1F0068] truncate flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#FF5C39]" /> {d.customerName}</div>
                <div className="text-xs text-[#1F0068]/70">{d.error || 'Send failed'}</div>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => sendOne(d)} className="rounded-full h-8 text-xs">Retry</Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss(d.id)} className="rounded-full h-8 w-8 p-0 text-[#1F0068]/60"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!resendEnabled && drafts.length > 0 && (
        <div className="mt-4 text-[11px] text-[#1F0068]/70 bg-[#FFF6EB] rounded-xl p-3">
          <b className="text-[#1F0068]">Tip:</b> Resend is not yet connected — clicking <b>Send</b> will queue the email and prompt to add your API key. Until then, use <b>Copy</b> to paste the designed email into your inbox.
        </div>
      )}
    </div>
  );
};

export default RenewalQueueWidget;
